import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion"
import { useNavigate, useParams } from "react-router-dom";
import { useArticleBySlug, useUpdateArticle } from "../queries/blogs/articles.query";
import { calculateReadingTime, cleanArticleHtml, cleanFooterLinks, generateArticleFooter, insertMetaIntoContent, optimizeArticleHtml } from "../helper/articleHelpers";
import { useDispatch, useSelector } from "react-redux";
import * as actions from '../redux/actions/billing';
import { PricingPlanModal } from "../components/PricingPlanModal";
import { DistributionModal } from "../components/DistributionAccess/DistributionModal";
import { ArticlePageSkeletonLoader } from "../components/ArticlePageSkeletonLoader";
import { generateBreadcrumbSchema, generateGraphSchema } from "../seo/schemaGenerator";
import { generateMetadata } from "../seo/metadataGenerator";
import SeoHead from "../seo/SeoHead";
import { isPrerendering } from "../helper/isPrerendering";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function ArticlePage() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { slug } = useParams();
    const [isLoad, setIsLoad] = useState(false)
    const [showDistributionModal, setShowDistributionModal] = useState(false);
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const cursorRef = useRef(null);
    const isHoveringRef = useRef(false);
    const { subscriptionPlan, hasActiveSubscription } = useSelector((state) => state.billing);
    const token = localStorage.getItem("authToken");

    const planList = subscriptionPlan || [];
    const monthlyPlan = planList.find(item => item.interval === "monthly") || null;
    const lastSubscriptionFetched = subscriptionPlan?.lastFetched;

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            if (cursorRef.current) {
                cursorRef.current.style.left = `${e.clientX}px`;
                cursorRef.current.style.top = `${e.clientY}px`;
            }
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = "none";

        const handleMouseEnter = () => {
            isHoveringRef.current = true;
            cursorRef.current?.classList.add("hovering");
        };
        const handleMouseLeave = () => {
            isHoveringRef.current = false;
            cursorRef.current?.classList.remove("hovering");
        };

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((el) => {
            el.addEventListener("mouseenter", handleMouseEnter);
            el.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        return () => {
            document.body.style.cursor = "auto";
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((el) => {
                el.removeEventListener("mouseenter", handleMouseEnter);
                el.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);

    const hasFetched = useRef(false);
    useEffect(() => {
        const fetchPlans = async () => {
            const shouldFetch =
                !planList?.length ||
                !lastSubscriptionFetched ||
                Date.now() - lastSubscriptionFetched > 1000 * 60 * 30; // 30 mins cache

            if (shouldFetch && !hasFetched.current && !isPrerendering()) {
                hasFetched.current = true; // prevent duplicate fetches
                await dispatch(actions.getSubscriptionPlans());
            }
        };

        fetchPlans();

        // reset flag when component unmounts or on logout, if needed
        return () => {
            hasFetched.current = false;
        };
    }, [dispatch, planList?.length, lastSubscriptionFetched]);

    const handleSubscription = async () => {
        if (token) {
            try {
                setIsLoad(true);
                if (hasActiveSubscription) {
                    await dispatch(actions.getCustomerPortalSession());
                } else {
                    setShowPricingPlanModal(true);
                }
            } catch (err) {
                console.error('Error handling Pro button:', err);
            } finally {
                setIsLoad(false);
            }
        } else {
            navigate('/signup')
        }
    }

    const { data: article, isLoading } = useArticleBySlug(slug);
    const { mutate: updateArticleStats } = useUpdateArticle();
    const articleContentRef = useRef(null);
    const hasRecordedViewRef = useRef(false);

    usePrerenderReady(!isLoading && !!article?.data);

    useEffect(() => {
        hasRecordedViewRef.current = false;
    }, [slug]);

    useEffect(() => {
        const articleId = article?.data?._id;
        if (!articleId || isLoading || isPrerendering()) return;

        const recordViewIfNeeded = () => {
            if (hasRecordedViewRef.current) return;

            const contentEl = articleContentRef.current;
            if (!contentEl) return;

            const rect = contentEl.getBoundingClientRect();
            const scrolledPast = window.innerHeight - rect.top;
            const progress = scrolledPast / rect.height;

            if (progress >= 0.75) {
                hasRecordedViewRef.current = true;
                updateArticleStats({ id: articleId, data: { incrementViews: true } });
            }
        };

        recordViewIfNeeded();
        window.addEventListener("scroll", recordViewIfNeeded, { passive: true });
        window.addEventListener("resize", recordViewIfNeeded);

        return () => {
            window.removeEventListener("scroll", recordViewIfNeeded);
            window.removeEventListener("resize", recordViewIfNeeded);
        };
    }, [article?.data?._id, isLoading, updateArticleStats]);

    const cleanedHtml = cleanArticleHtml(article?.data?.contentHtml || "");
    const readingTime = calculateReadingTime(article?.data?.contentMarkdown || "");

    const formattedPublishDate = new Date(
        article?.data?.publishedAt || new Date()
    ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    const articleMetaHtml = `
  <div class="article-meta">
    <div class="article-meta-top">
      <span class="article-category">${article?.data?.category}</span>
      <span class="article-reading-time">${readingTime}</span>
    </div>

    <div class="article-publish-date">
      Published on ${formattedPublishDate} / by ${article?.data?.author}
    </div>

    <div class="article-excerpt">
      ${article?.data?.metaDescription || ""}
    </div>
  </div>
`;

    const articleHtml = cleanFooterLinks(insertMetaIntoContent(cleanedHtml, articleMetaHtml))
    const footerHtml = generateArticleFooter({ keywords: article?.data.keywords || [], });
    const contentHtml = `
        ${articleHtml}
        ${footerHtml}
    `.replace(
        /<img/g,
        '<img loading="lazy" decoding="async"'
    );

    const optimizedArticle = optimizeArticleHtml(contentHtml, article?.data?.heroImageUrl)

    const articleMetadata = generateMetadata({
        title: article?.data?.title ? `${article.data.title} | UpNComer` : undefined,
        description: article?.data?.metaDescription || "",
        image: article?.data?.heroImageUrl || undefined,
        path: `/blog/${slug}`,
    });

    const breadcrumbItems = [
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Blog",
            "path": "/blog"
        },
        {
            "name": article?.data?.title,
            "path": `/blog/${slug}`
        }
    ]

    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems)

    const normalizeSchema = (schema) => {
        if (!schema) return [];
        if (Array.isArray(schema)) return schema.flatMap(normalizeSchema);
        if (schema["@graph"] && Array.isArray(schema["@graph"])) {
            return schema["@graph"].flatMap(normalizeSchema);
        }
        return [schema];
    };

    const articleSchemas = generateGraphSchema([
        ...normalizeSchema(article?.data?.jsonLd).filter(
            (s) => s?.["@type"] !== "BreadcrumbList"
        ),
        ...normalizeSchema(article?.data?.faqJsonLd),
        breadcrumbSchema,
    ]);

    const helmetNode = (
        <SeoHead
            metadata={articleMetadata}
            ogType="article"
            schemas={articleSchemas}
        />
    );

    // Show skeleton loader while loading
    if (isLoading) {
        return <>{helmetNode}<ArticlePageSkeletonLoader /></>;
    }

    return (
        <>
            <div
                ref={cursorRef}
                className="cursor-spotlight"
                style={{ transform: "translate(-50%, -50%)", position: "fixed", pointerEvents: "none" }}
            >
                <div className="inner-circle"></div>
            </div>
            <div
                className="transition-all duration-1000 opacity-100 blur-none"
            >
                <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
                    {/* Enhanced Multi-layered Background */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                        {/* Floating orbs */}
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                            }}
                            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                        />

                        <motion.div
                            animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.2, 0.5, 0.2],
                            }}
                            transition={{
                                duration: 12,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                                delay: 3,
                            }}
                            className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                        />
                    </div>
                    <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
                        <div className="container max-w-7xl mx-auto">
                            {/* SEO + SCHEMA */}
                            {helmetNode}

                            {/* ARTICLE CONTENT */}
                            <article
                                ref={articleContentRef}
                                className="
    article-content
    font-jakarta
    max-w-4xl
    mx-auto
    px-4
    md:px-6
    lg:px-8
  "
                            >

                                <div
                                    className="
      prose
      prose-lg
      max-w-none

      prose-headings:font-bold
      prose-headings:text-white

      prose-h1:text-4xl
      prose-h1:leading-tight
      prose-h1:mb-6

      prose-h2:text-3xl
      prose-h2:mt-14
      prose-h2:mb-6
      prose-h2:border-b
      prose-h2:border-gray-200
      prose-h2:pb-3

      prose-h3:text-2xl
      prose-h3:mt-10
      prose-h3:mb-4

      prose-p:text-white
      prose-p:leading-8
      prose-p:mb-6

      prose-a:text-theme_clr2
      prose-a:no-underline
      hover:prose-a:underline

      prose-strong:text-white

      prose-img:rounded-2xl
      prose-img:shadow-lg
      prose-img:my-8

      prose-ul:my-6
      prose-ol:my-6

      prose-li:my-2
      prose-li:text-white
      prose-li:marker:text-white

      prose-blockquote:border-l-4
      prose-blockquote:border-theme_clr2
      prose-blockquote:bg-theme_clr1
      prose-blockquote:px-6
      prose-blockquote:py-4
      prose-blockquote:rounded-r-xl
      prose-blockquote:italic

      prose-table:block
      prose-table:overflow-x-auto

      prose-th:bg-theme_clr2
      prose-th:text-white
      prose-th:p-3

      prose-td:p-3
      prose-td:text-white

      prose-code:text-pink-600

      prose-pre:bg-gray-900
      prose-pre:text-gray-100

      prose-hr:my-12
    "
                                    dangerouslySetInnerHTML={{
                                        __html: optimizedArticle,
                                    }}
                                />

                            </article>
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.8 }}
                            className="text-center"
                        >
                            <h2 className="text-4xl font-jakarta font-bold text-[#c7d2fe] mb-6">Ready to Build Your Music Empire?</h2>
                            {/* <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto">
                            Join hundreds of independent artists who are already growing their careers with UpNComer.
                        </p> */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={handleSubscription}
                                    className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                         px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                         transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                                >
                                    BECOME AN UPNCOMER
                                </button>
                                <button
                                    onClick={() => setShowDistributionModal(true)}
                                    className="inline-block bg-white text-theme_clr2 text-center
                             px-5 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                             transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105
                             backdrop-blur-sm border border-white/10"
                                    style={{
                                        boxShadow: "0 10px 30px rgba(250, 97, 56, 0.3)",
                                    }}
                                >
                                    DISTRIBUTE YOUR MUSIC
                                </button>
                            </div>
                        </motion.div>
                    </main>
                </div>
            </div>
            {/* Distribution Modal */}
            <DistributionModal
                isOpen={showDistributionModal}
                onClose={() => setShowDistributionModal(false)}
            />
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/dashboard"} />
        </>
    )
}